// The Dock: a persistent instruction bar on feed screens. "Talking to: <scope>", ↑/↓ walk the
// scope ladder (card → this sweep → feed → configuration) while the input is empty, `.` focuses
// it, Enter queues an InstructionRequest. Instructions prepare or research; they never send.
// On phones it collapses to a bar that opens a bottom sheet.
// Adapted from EveryInc/tend src/shell/Dock.tsx (MIT, Copyright (c) 2026 Every, Inc.): no
// Claude/Codex toggle, no push-to-talk (OS dictation works in the textarea), typed scopes.
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, MessageSquareText, X } from "lucide-react";
import { toast } from "sonner";
import { isApiError } from "@web/lib/api";
import { cn } from "@web/lib/cn";
import { feedApi } from "@web/lib/feedApi";
import { feedKeys, useActiveFeed, useBoard } from "@web/lib/feedQueries";
import { useMediaQuery } from "@web/lib/useMediaQuery";
import { registerUndo } from "@web/lib/undoRegistry";
import { cancelWork } from "@web/feed/cardOps";
import { DictateButton } from "@web/compose/DictateButton";
import { Button } from "@web/ui/Button";
import { Kbd } from "@web/ui/Kbd";
import { useCommand } from "./commands";
import {
  defaultDocKey,
  dock,
  effectiveScope,
  scopeLabel,
  scopeLadder,
  scopeOutcome,
  scopeTarget,
  useDock,
  zoomScope,
} from "./dockStore";

function DockForm({ onSubmitted, autoFocus }: { onSubmitted?: () => void; autoFocus?: boolean }) {
  const queryClient = useQueryClient();
  const state = useDock();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const ladder = scopeLadder(state);
  const scope = effectiveScope(state.scope, state);
  const index = ladder.indexOf(scope);
  const label = scopeLabel(state.scope, state);
  const docs = useQuery({
    queryKey: feedKeys.docs,
    queryFn: ({ signal }) => feedApi.docs(signal),
    enabled: scope === "configuration",
    staleTime: 60_000,
  });
  const docKey = state.docKey ?? (state.feedId ? defaultDocKey(state.feedId) : "");

  useEffect(() => {
    if (state.focusSignal || autoFocus) inputRef.current?.focus({ preventScroll: true });
  }, [state.focusSignal, autoFocus]);

  const zoom = (direction: "broader" | "narrower") =>
    dock.setScope(zoomScope(ladder, scope, direction));

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    const instruction = value.trim();
    const target = scopeTarget(state.scope, state);
    if (!instruction || !state.feedId || !target || busy) return;
    setBusy(true);
    try {
      const work = await feedApi.instruct(state.feedId, { instruction, target });
      setValue("");
      inputRef.current?.blur();
      onSubmitted?.();
      registerUndo({
        message: scopeOutcome(state.scope, state),
        undo: () => cancelWork(queryClient, work.id),
        doneMessage: "Instruction cancelled.",
        tooLateMessage: "Too late to cancel: Claude already started.",
      });
      void queryClient.invalidateQueries({ queryKey: ["work"] });
      void queryClient.invalidateQueries({ queryKey: ["board"] });
    } catch (error) {
      toast(
        isApiError(error, "not_available")
          ? "Claude isn't wired up yet. Your note is still here."
          : isApiError(error, "stale")
            ? "The round changed. Your note is still here; send it again."
            : "Couldn't queue that. Your note is still here.",
      );
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submit();
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      e.currentTarget.blur();
      return;
    }
    const plain = !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey;
    if ((e.key === "ArrowUp" || e.key === "ArrowDown") && plain && !value.trim()) {
      e.preventDefault();
      zoom(e.key === "ArrowUp" ? "broader" : "narrower");
    }
  };

  return (
    <form
      onSubmit={(e) => void submit(e)}
      aria-label="Talk to Claude"
      className={cn(
        `mm-scope-${scope} mm-scope-rule mx-auto w-full max-w-4xl rounded-xl border border-line-strong bg-paper px-3 py-2`,
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
        <span>Talking to:</span>
        <b
          key={`${scope}:${label}`}
          data-testid="dock-scope"
          data-scope={scope}
          className="mm-scope-chip mm-scope-pulse max-w-[min(28rem,60vw)] truncate rounded-full px-2 py-0.5 font-medium"
        >
          {label}
        </b>
        {scope === "configuration" ? (
          <select
            aria-label="Which document"
            value={docKey}
            onChange={(e) => {
              const doc = docs.data?.find((d) => d.key === e.target.value);
              dock.setDoc(e.target.value, doc?.title ?? null);
            }}
            className="h-6 max-w-48 rounded-md border border-line bg-paper px-1 text-xs text-ink"
          >
            {!docs.data?.some((d) => d.key === docKey) && docKey ? (
              <option value={docKey}>{state.docTitle ?? "Feed policy"}</option>
            ) : null}
            {docs.data?.map((d) => (
              <option key={d.key} value={d.key}>
                {d.title}
              </option>
            ))}
          </select>
        ) : null}
        <div className="ml-auto flex items-center gap-1">
          <span className="mm-kicker hidden !text-[10px] sm:inline">Scope</span>
          <button
            type="button"
            aria-label="Broader scope"
            title="Broader scope (↑ when empty)"
            disabled={index >= ladder.length - 1}
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => zoom("broader")}
            className="inline-flex h-6 items-center gap-0.5 rounded-md border border-line px-1.5 text-muted hover:text-ink disabled:opacity-40"
          >
            <ChevronUp aria-hidden className="size-3.5" />
            <span className="hidden sm:inline">Broader</span>
          </button>
          <button
            type="button"
            aria-label="Narrower scope"
            title="Narrower scope (↓ when empty)"
            disabled={index <= 0}
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => zoom("narrower")}
            className="inline-flex h-6 items-center gap-0.5 rounded-md border border-line px-1.5 text-muted hover:text-ink disabled:opacity-40"
          >
            <ChevronDown aria-hidden className="size-3.5" />
            <span className="hidden sm:inline">Narrower</span>
          </button>
        </div>
      </div>
      <div className="mt-1.5 flex items-end gap-2">
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          aria-label={`Instruction for Claude about ${label}`}
          placeholder="Tell Claude what to notice, change, or look into…"
          className="max-h-40 min-h-10 flex-1 resize-none rounded-lg bg-sunk/70 px-3 py-2 text-[15px] text-ink placeholder:text-faint"
        />
        {/* U5 dictation (Chrome only, when turned on): the words land in the input to edit. */}
        <DictateButton
          className="self-center"
          onDone={(words) => {
            setValue((v) => (v.trim() ? `${v.trimEnd()} ${words}` : words));
            inputRef.current?.focus({ preventScroll: true });
          }}
        />
        <Button
          type="submit"
          variant="primary"
          busy={busy}
          disabled={!value.trim() || !state.feedId}
        >
          Ask Claude
        </Button>
      </div>
      <p className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-muted">
        <span>Instructions prepare or research. To send, press S on a card.</span>
        <span className="hidden md:inline">
          <Kbd className="h-4 min-w-4 text-[10px]">↑</Kbd>/
          <Kbd className="h-4 min-w-4 text-[10px]">↓</Kbd> scope when empty ·{" "}
          <Kbd className="h-4 min-w-4 text-[10px]">Enter</Kbd> ask
        </span>
      </p>
    </form>
  );
}

export function Dock() {
  const { feedId, feed } = useActiveFeed();
  const board = useBoard(feedId);
  const state = useDock();
  const phone = !useMediaQuery("(min-width: 768px)");
  const [sheet, setSheet] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  // Publish the Dock's height so toasts sit above it rather than on top of the input.
  useEffect(() => {
    const el = barRef.current;
    const root = document.documentElement;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      root.style.setProperty("--mm-dock-h", `${Math.round(el.getBoundingClientRect().height)}px`);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--mm-dock-h");
    };
  }, [phone]);

  useEffect(() => {
    dock.setFeed(
      feedId,
      board.data?.feed.name ?? feed?.name ?? null,
      board.data?.sweep.currentBatchId ?? null,
    );
  }, [feedId, feed?.name, board.data?.feed.name, board.data?.sweep.currentBatchId]);

  useCommand("feed.dock", () => {
    if (phone) setSheet(true);
    dock.focus();
  });

  if (!phone) {
    return (
      <div ref={barRef} className="shrink-0 border-t border-line bg-bg px-4 py-2 md:px-6">
        <DockForm />
      </div>
    );
  }

  const scope = effectiveScope(state.scope, state);
  return (
    <>
      <div ref={barRef} className="shrink-0 border-t border-line bg-bg px-3 py-1.5">
        <button
          type="button"
          onClick={() => setSheet(true)}
          className={`mm-scope-${scope} flex h-10 w-full items-center gap-2 rounded-xl border border-line-strong bg-paper px-3 text-left text-sm`}
        >
          <MessageSquareText aria-hidden className="size-4 text-muted" />
          <span className="shrink-0 whitespace-nowrap text-muted">Talk to Claude ·</span>
          <span className="mm-scope-chip min-w-0 truncate rounded-full px-2 py-0.5 text-xs font-medium">
            {scopeLabel(state.scope, state)}
          </span>
        </button>
      </div>
      {sheet ? (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          <button
            type="button"
            aria-label="Close"
            className="absolute inset-0 bg-black/25"
            onClick={() => setSheet(false)}
          />
          <div className="relative rounded-t-2xl border-t border-line bg-bg px-3 pt-2 pb-[max(12px,env(safe-area-inset-bottom))] shadow-soft">
            <div className="mb-2 flex items-center justify-between">
              <span aria-hidden className="mx-auto h-1 w-10 rounded-full bg-line-strong" />
              <button
                type="button"
                aria-label="Close"
                onClick={() => setSheet(false)}
                className="absolute top-2 right-3 rounded-md p-1 text-muted hover:bg-sunk"
              >
                <X className="size-4" />
              </button>
            </div>
            <DockForm autoFocus onSubmitted={() => setSheet(false)} />
          </div>
        </div>
      ) : null}
    </>
  );
}
